import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Check, Clock, Heart, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NotificationExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewOptions: () => void;
  triggerContext?: 'check-in' | 'habit' | 'general';
}

export const NotificationExplainerModal: React.FC<NotificationExplainerModalProps> = ({
  isOpen,
  onClose,
  onViewOptions,
  triggerContext = 'check-in'
}) => {
  const contextMessages = {
    'check-in': {
      title: 'Stay on track with gentle reminders',
      subtitle: 'You just completed your first check-in! Would you like reminders to help you build this habit?'
    },
    'habit': {
      title: 'Never miss your habits',
      subtitle: 'Notifications can help you stay consistent with your wellness goals.'
    },
    'general': {
      title: 'Get the most from Therappi',
      subtitle: 'Optional notifications help you stay connected to your wellness journey.'
    }
  };

  const benefits = [
    { icon: Clock, text: 'Daily check-in reminders at your preferred time' },
    { icon: Heart, text: 'Gentle prompts for mindfulness and self-care' },
    { icon: Sparkles, text: 'Celebrate your streaks and achievements' }
  ];

  const { title, subtitle } = contextMessages[triggerContext];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-sm bg-card rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header with icon */}
            <div className="relative pt-8 pb-4 px-6 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {subtitle}
              </p>
            </div>

            {/* Benefits list */}
            <div className="px-6 pb-4 space-y-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <benefit.icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm text-foreground pt-1.5">{benefit.text}</p>
                </div>
              ))}
            </div>

            {/* Reassurance text */}
            <div className="px-6 pb-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground text-center">
                  <Check className="w-3 h-3 inline-block mr-1 text-primary" />
                  Notifications are optional and fully customisable. 
                  Nothing is turned on automatically.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 space-y-3">
              <Button
                onClick={onViewOptions}
                className="w-full"
                size="lg"
              >
                See notification options
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="w-full text-muted-foreground"
                size="lg"
              >
                Not now
              </Button>
            </div>

            {/* Footer note */}
            <div className="px-6 pb-4">
              <p className="text-[11px] text-muted-foreground/70 text-center">
                You can come back and change these settings anytime in Settings → Notifications
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationExplainerModal;
